<template>
  <button
    :disabled="disabled"
    :class="[variant, classes, 'rbz-button', { 'icon-only': !label }]"
    :data-qa="dataQa"
    tabindex="0"
    @click="
      addMetadata($event);
      clearFocus($event);
      emit('button-click', $event);
    "
    @focus="emit('focus', $event)"
    @blur="emit('blur', $event)"
  >
    <div
      class="spinner is-loading"
      v-if="isLoading"
      :style="`--spinner-size:16px; --spinner-width:2.5px`"
    ></div>
    <component
      v-if="!isLoading && buttonIcon && iconPosition === 'left'"
      :is="buttonIcon"
      v-bind="iconProps"
      class="button-icon"
    />
    <span v-if="!!label" class="ellipsis">
      {{ label }}
    </span>
    <component
      v-if="!isLoading && buttonIcon && iconPosition === 'right'"
      :is="buttonIcon"
      v-bind="iconProps"
      class="button-icon"
    />
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { getIconComponent } from '../../assets/icons';
import { FormButtonVariant, GenericObject } from '../../rbz/types';

// Required label or icon
interface FormButtonInnerTemplateProps {
  label?: string;
  icon?: string;
  iconProps?: GenericObject;
  iconPosition?: 'left' | 'right';
  classes?: string;
  disabled?: boolean;
  variant?: FormButtonVariant;
  dataQa?: string;
  skipFocusClear?: boolean;
  isLoading?: boolean;
}

const props = withDefaults(defineProps<FormButtonInnerTemplateProps>(), {
  label: '',
  icon: '',
  iconProps: null,
  classes: '',
  tooltip: '',
  iconPosition: 'left',
  disabled: false,
  variant: 'primary',
  dataQa: '',
});

interface FormButtonInnerTemplateEmits {
  (e: 'button-click', value: Event): void;

  (e: 'focus', value: Event): void;

  (e: 'blur', value: Event): void;
}

const emit = defineEmits<FormButtonInnerTemplateEmits>();

const buttonIcon = computed(() => {
  if (props.variant === 'negative') return getIconComponent('IconDelete');
  if (props.icon) return getIconComponent(props.icon);
  return '';
});

const addMetadata = (event: Event) => {
  event.originatedFromButton = true;
};

const clearFocus = (event: Event) => {
  if (!props.skipFocusClear && event.pointerType === 'mouse') {
    (event.currentTarget as HTMLElement)?.blur();
  }
};
</script>

<style scoped lang="scss">
@use 'sass:map';
@use '../../assets/styles/colors' as colors;

.rbz-button {
  align-items: center;
  border-radius: 4px;
  box-sizing: border-box;
  color: map.get(colors.$colors, 'white');
  cursor: pointer;
  display: flex;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  gap: 4px;
  height: 40px;
  line-height: 18px;
  padding: 8px 12px;
  position: relative;
  text-transform: capitalize;
  width: fit-content;

  &:focus-visible {
    outline: none;
  }

  &:disabled {
    opacity: 0.3;
    box-shadow: none;
    cursor: default;
  }

  &.icon-only {
    padding: 8px;
  }

  &.original-text {
    text-transform: none;
  }
}

.primary {
  background-color: map.get(colors.$colors, 'jewel');
  border: 0;
  color: map.get(colors.$colors, 'white');

  :deep(.button-icon) {
    fill: map.get(colors.$colors, 'white');
  }

  &:hover {
    background-color: map.get(colors.$colors, 'mountain-meadow-light');
  }

  &:active {
    background-color: map.get(colors.$colors, 'stratos');
  }

  &:focus:not(:active) {
    background-color: map.get(colors.$colors, 'jewel');
    box-shadow: 0 0 0 4px map.get(colors.$colors, 'magic-mint');
    outline: none;
    z-index: 2;
  }

  &:disabled:hover,
  &:disabled:active {
    background-color: map.get(colors.$colors, 'jewel');
  }
}

.negative {
  background-color: map.get(colors.$colors, 'white');
  border: 1px solid map.get(colors.$colors, 'crimson');
  color: map.get(colors.$colors, 'crimson');
  padding: 7px 11px;

  :deep(.button-icon) {
    fill: map.get(colors.$colors, 'crimson');
  }

  &:hover,
  &:active {
    background-color: map.get(colors.$colors, 'linen');
    border: 1px solid map.get(colors.$colors, 'crimson');
    color: map.get(colors.$colors, 'crimson');
  }

  &:focus:not(:active) {
    background-color: map.get(colors.$colors, 'linen');
    border: 1px solid map.get(colors.$colors, 'crimson');
    box-shadow: 0 0 0 2px rgb(232, 72, 72, 0.25);
    color: map.get(colors.$colors, 'crimson');
    outline: none;
    z-index: 2;
  }

  &:disabled:hover,
  &:disabled:active {
    background-color: map.get(colors.$colors, 'white');
    border: 1px solid map.get(colors.$colors, 'crimson');
    color: map.get(colors.$colors, 'crimson');
  }
}

.secondary {
  background-color: map.get(colors.$colors, 'white');
  border: 1px solid map.get(colors.$colors, 'jewel');
  color: map.get(colors.$colors, 'jewel');
  padding: 7px 11px;

  :deep(.button-icon) {
    fill: map.get(colors.$colors, 'jewel');
  }

  &:hover {
    background-color: map.get(colors.$colors, 'mountain-meadow-light');
    border: 0;
    color: map.get(colors.$colors, 'white');
    padding: 8px 12px;

    :deep(.button-icon) {
      fill: map.get(colors.$colors, 'white');
    }
  }

  &:active {
    background-color: map.get(colors.$colors, 'stratos');
    border: 0;
    color: map.get(colors.$colors, 'white');
    padding: 8px 12px;

    :deep(.button-icon) {
      fill: map.get(colors.$colors, 'white');
    }
  }

  &:focus:not(:active) {
    background-color: map.get(colors.$colors, 'jewel');
    border: 0;
    box-shadow: 0 0 0 4px map.get(colors.$colors, 'magic-mint');
    color: map.get(colors.$colors, 'white');
    outline: none;
    padding: 8px 12px;
    z-index: 2;

    :deep(.button-icon) {
      fill: map.get(colors.$colors, 'white');
    }
  }

  &:disabled:hover,
  &:disabled:active {
    background-color: map.get(colors.$colors, 'white');
    border: 1px solid map.get(colors.$colors, 'jewel');
    color: map.get(colors.$colors, 'jewel');
  }

  &:disabled :deep(.button-icon) {
    fill: map.get(colors.$colors, 'jewel');
  }

  &.icon-only {
    &:hover,
    &:active,
    &:focus:not(:active) {
      padding: 8px;
    }
  }
}

.tertiary {
  background-color: map.get(colors.$colors, 'white');
  border: 0;
  color: map.get(colors.$colors, 'jewel');

  :deep(.button-icon) {
    fill: map.get(colors.$colors, 'jewel');
  }

  &:hover {
    background-color: map.get(colors.$colors, 'mountain-meadow-light');
    color: map.get(colors.$colors, 'white');

    :deep(.button-icon) {
      fill: map.get(colors.$colors, 'white');
    }
  }

  &:active {
    background-color: map.get(colors.$colors, 'stratos');
    color: map.get(colors.$colors, 'white');

    :deep(.button-icon) {
      fill: map.get(colors.$colors, 'white');
    }
  }

  &:focus:not(:active) {
    background-color: map.get(colors.$colors, 'jewel');
    box-shadow: 0 0 0 4px map.get(colors.$colors, 'magic-mint');
    color: map.get(colors.$colors, 'white');
    outline: none;
    z-index: 2;

    :deep(.button-icon) {
      fill: map.get(colors.$colors, 'white');
    }
  }

  &:disabled:hover,
  &:disabled:active {
    background-color: map.get(colors.$colors, 'white');
    color: map.get(colors.$colors, 'jewel');
  }

  &:disabled :deep(.button-icon) {
    fill: map.get(colors.$colors, 'jewel');
  }
}
</style>
